'use client'; // Add this at the top

import React, { useState } from 'react';
import { useAOS } from '@/hooks/useAOS';
// FAQ answers are hardcoded, dev-authored HTML (see MainPage `faqs`) — trusted content, so we
// render directly and avoid pulling the heavy sanitize-html (+postcss+htmlparser2) into the
// homepage bundle. Any CMS/user-sourced HTML must still go through @/lib/sanitize.

// FaqItem component for individual FAQ items
const FaqItem = ({ question, answer, isOpen, onToggle, isLast }) => {
    const panelId = React.useId();
    return (
        <div
            className={`transition-all duration-200 py-[16px] max-w-[800px]  border-b-2 border-[#D8E9FF] cursor-pointer ${isLast ? 'border-none' : ''}`}
        >
            <button
                type="button"
                aria-expanded={isOpen}
                aria-controls={panelId}
                className="flex items-center text-start  justify-between h-fit   w-full"
                onClick={onToggle}
            >
                <span
                    className={`${isOpen ? 'text-[#195AFF]' : 'text-[#090D28]'
                        } schibsted-grotesk text-[18px] md:text-[20px] font-semibold leading-[30px]`}
                >
                    {question}
                </span>
                <div className='w-[20px]'>
                    <svg
                        className={`w-6 h-6 transition-transform duration-300 ${isOpen ? 'rotate-180 text-[#195AFF]' : 'text-gray-400'
                            }`}
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                        />
                    </svg>
                </div>
            </button>
            <div
                id={panelId}
                role="region"
                aria-label={question}
                className={`grid transition-[grid-template-rows] duration-300 ease-in-out font-Schibsted_Grotesk ${isOpen ? 'grid-rows-[1fr] font-Schibsted_Grotesk' : 'grid-rows-[0fr] font-Schibsted_Grotesk'
                    }`} >
                <div
                    className="overflow-hidden text-[#2D3238] mt-[8px] font-Schibsted_Grotesk text-[16px] leading-[150%] [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1 [&_strong]:font-bold [&_strong]:font-Schibsted_Grotesk [&_b]:font-bold [&_b]:font-Schibsted_Grotesk"
                    dangerouslySetInnerHTML={{ __html: answer }}
                />
            </div>
        </div>
    );
};

// FaqSection component
const FaqSection = ({ faqs, initialShowAll = false }) => {
    const [openIndex, setOpenIndex] = useState(0); // Independent state for each section
    const [showAll, setShowAll] = useState(initialShowAll);

    const visibleFaqs = showAll ? faqs : faqs.slice(0, 5);

    // Toggle function for smooth open/close
    const toggleFaq = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    useAOS();

    return (
        <div >
            <section className="flex flex-col max-w-7xl mx-auto gap-[40px] md:pb-[10px] pb-[50px]">
                <div className=''>

                    <h2 className="md:mt-[80px] mt-[40px] text-[#090D28] flex items-center justify-center text-center text-[32px] md:text-[48px] font-extrabold leading-[120%] font-NeueMontreal md:text-nowrap text-wrap">
                        Your questions&nbsp;Our Answers
                    </h2>
                </div>
                <div className=" mx-auto max-w-7xl md:px-0 px-4">
                    <div className=" mx-auto">
                        {visibleFaqs.map((faq, index) => (
                            <FaqItem
                                key={index}
                                question={faq.question}
                                answer={faq.answer}
                                isOpen={openIndex === index}
                                onToggle={() => toggleFaq(index)}
                                isLast={index === visibleFaqs.length - 1}
                            />
                        ))}

                        {faqs.length > 5 && (
                            <div className="text-center mt-4 mb-4">
                                <button
                                    onClick={() => setShowAll(!showAll)}
                                    className="mt-2 font-Schibsted_Grotesk inline-flex h-[48px] px-[24px] py-[16px] justify-center items-center flex-shrink-0 text-[#195AFF] text-[16px] md:text-[18px] font-semibold leading-[120%] rounded-[8px] bg-white shadow-[-11px_34px_10px_0px_rgba(25,90,255,0),-7px_21px_9px_0px_rgba(25,90,255,0.01),-4px_12px_8px_0px_rgba(25,90,255,0.05),-2px_5px_6px_0px_rgba(25,90,255,0.09),0px_1px_3px_0px_rgba(25,90,255,0.10)]"
                                >
                                    {showAll ? 'Show Less' : 'Show More'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default FaqSection;